<template>
  <div
    class="zoox-component zoox-photo-upload"
    :class="{rounded, dashed, 'with-points': withPoints}"
  >
    <v-file-input
      v-if="!image"
      ref="input"
      v-bind="$attrs"
      type="file"
      name="image"
      accept="image/*"
      outlined
      dense
      :prepend-icon="inputIcon"
      @change="setImage"
    ></v-file-input>
    <template v-else>
      <vue-cropper
        ref="cropper"
        drag-mode="move"
        :view-mode="1"
        :aspect-ratio="rounded ? 1 : aspectRatio"
        :src="image"
        :img-style="imgStyle"
        :container-style="containerStyle"
        :auto-crop-area="cropWholeImage ? 1 : 0.8"
        alt="Source Image"
      >
      </vue-cropper>
      <div class="tip">{{ $t('move_to_adjust') }}</div>
    </template>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs'

/**
 * Componente tem por objetivo fazer com que o usuário faça upload da foto e também
 * pré-visualize e corte a foto para adequar melhor ao objetivo dele.
 * @displayName Photo Upload
 */
export default {
  name: 'ZPhotoUpload',
  components: {VueCropper},
  model: {
    prop: 'image',
    event: 'change',
  },
  props: {
    /**
     * Imagem a ser exibida pelo componente
     *
     * @modelName image
     */
    image: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Adiciona bordas arredondadas ao cropper de imagem
     */
    rounded: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Define o aspect ratio do cropper
     */
    aspectRatio: {
      type: [Number, String],
      required: false,
      default: null,
    },
    /**
     * Define o ícone a ser exibido no input
     */
    inputIcon: {
      type: String,
      required: false,
      default: '$attach_file_rounded',
    },
    /**
     * Style do container feito bind com a imagem
     */
    containerStyle: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Style feito bind da imagem
     */
    imgStyle: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Flag para indicar o uso das linhas de corte
     */
    dashed: {
      type: Boolean,
      default: false,
    },
    withPoints: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar que a área de corte deve ser a imagem inteira
     */
    cropWholeImage: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    setImage(file) {
      if (file.type.indexOf('image/') === -1) {
        return
      }

      if (typeof FileReader === 'function') {
        const reader = new FileReader()

        reader.onload = (event) => {
          /**
           * Evento emitido quando o reader termina de ler o arquivo
           *
           * @property {String} image Imagem carregada pelo componente
           */
          this.$emit('change', event.target.result)
        }
        reader.readAsDataURL(file)
      }
    },
    /**
     * @public
     *
     * Define a area de corte da imagem
     */
    setCropBoxData(data = {}) {
      if (!this.$refs.cropper) return
      const currentCropBoxData = this.$refs.cropper.getCropBoxData()

      this.$refs.cropper.setCropBoxData({...currentCropBoxData, ...data})
    },

    /**
     * @public
     *
     * Retorna a imagem cortada
     */
    getCroppedImage() {
      if (this.image) {
        const croppedCanvas = this.$refs.cropper.getCroppedCanvas()

        if (!this.rounded) {
          return croppedCanvas.toDataURL()
        } else {
          return this.getRoundedImage(croppedCanvas).toDataURL()
        }
      }
    },

    getImage() {
      return this.$refs.cropper.getCroppedCanvas()
    },

    getRoundedImage(sourceCanvas) {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      const width = sourceCanvas.width
      const height = sourceCanvas.height

      canvas.width = width
      canvas.height = height
      context.imageSmoothingEnabled = true
      context.drawImage(sourceCanvas, 0, 0, width, height)
      context.globalCompositeOperation = 'destination-in'
      context.beginPath()
      context.arc(
        width / 2,
        height / 2,
        Math.min(width, height) / 2,
        0,
        2 * Math.PI,
        true
      )
      context.fill()
      return canvas
    },
  },
  i18n: {
    messages: {
      en: {
        move_to_adjust: 'Move to adust',
      },
      pt: {
        move_to_adjust: 'Mova para ajustar',
      },
      es: {
        move_to_adjust: 'Mover para ajustar',
      },
      fr: {
        move_to_adjust: 'Déplacer pour ajuster',
      },
      tr: {
        move_to_adjust: 'Uygun hale getir',
      },
    },
  },
}
</script>

<style lang="scss">
.zoox-photo-upload {
  position: relative;

  .tip {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    cursor: move;
    content: url('../../../assets/img/photo-upload-tip.png');
    min-width: 160px;
    width: 50%;
    max-width: 250px;
    opacity: 1;
  }

  &:hover {
    .tip {
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.8s, visibility 0.8s ease-in-out;
    }
  }

  &:not(.with-points) {
    .cropper-line {
      width: 0;
      background-color: unset;
    }

    .cropper-point {
      height: 2px;
      width: 2px;
      background-color: unset;
    }
  }

  &:not(.dashed) {
    .cropper-dashed {
      border: unset;
    }

    .cropper-line {
      width: 0;
      background-color: unset;
    }
  }

  &.rounded {
    .cropper-view-box,
    .cropper-face {
      border-radius: 50%;
      outline: unset;
    }
  }
}
</style>
